/** @type {(() => void) | null} */
let cancelPending = null;

const MODIFIER_KEYS = new Set(["shiftKey", "metaKey", "ctrlKey", "altKey"]);

/**
 * Wraps a mouse event so its modifier-key flags read as false, while delegating
 * everything else (including preventDefault/stopPropagation) to the real event.
 * A right-click should only select the row to anchor the menu; without this, a
 * select callback that reads shiftKey/metaKey (e.g. via EventUtils.selectionType)
 * would treat shift/cmd+right-click as a range/multi-select.
 *
 * @param {React.MouseEvent} event
 * @returns {React.MouseEvent}
 */
const withoutModifiers = (event) =>
  new Proxy(event, {
    get(target, prop) {
      if (MODIFIER_KEYS.has(prop)) return false;
      const value = target[prop];
      return typeof value === "function" ? value.bind(target) : value;
    },
  });

/**
 * Right-click handler for a left-sidepanel row. Selects the row first (so the
 * row's active state keeps the menu visible), then programmatically opens its
 * ContextMenuWrapper 3-dot menu.
 *
 * The button's open() call is deferred to the next `mouseup` so the
 * right-click's own mouseup doesn't immediately dismiss the popover as a
 * click-outside. On Windows the `contextmenu` event fires AFTER mouseup
 * (the button is already released, so `event.buttons === 0`), meaning no
 * mouseup is pending; in that case we fall back to opening on the next tick
 * so the menu doesn't wait for an unrelated future mouseup.
 *
 * @param {Object} args
 * @param {React.MouseEvent} args.event - The contextmenu event.
 * @param {HTMLElement|null|undefined} args.rowEl - The row element containing the 3-dot menu button.
 * @param {boolean} args.active - Whether the row is already selected.
 * @param {(event: React.MouseEvent) => void} [args.select] - Callback to select the row when not active.
 */
const openRowContextMenu = ({ event, rowEl, active, select }) => {
  event.preventDefault();
  event.stopPropagation();
  if (!active) select?.(withoutModifiers(event));

  cancelPending?.();

  if (!rowEl?.querySelector("[data-context-menu-trigger]")) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("openRowContextMenu: no [data-context-menu-trigger] found in row", rowEl);
    }
    return;
  }

  let settled = false;
  /** @type {number | null} */
  let frame = null;
  const cleanup = () => {
    settled = true;
    cancelPending = null;
    window.removeEventListener("mouseup", open, true);
    if (frame !== null) cancelAnimationFrame(frame);
  };
  const open = () => {
    if (settled) return;
    cleanup();
    /** @type {(Element & { open?: () => void }) | null | undefined} */
    const button = rowEl?.querySelector("[data-context-menu-trigger]");
    button?.open?.();
  };
  cancelPending = cleanup;
  window.addEventListener("mouseup", open, true);

  if (event.buttons === 0) {
    frame = requestAnimationFrame(open);
  }
};

export default openRowContextMenu;
